import { ArrowUpRight, Crown, Sparkles } from 'lucide-react';
import { clientWork, featuredProject, projects } from '@/lib/portfolio';
import { cn } from '@/lib/utils';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';

function TagPills({ tags }: { tags: string[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((tag) => (
        <span
          key={tag}
          className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-muted-foreground transition-colors group-hover:border-white/20"
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

function FeaturedCard() {
  return (
    <Reveal>
      <div className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-card/70 p-8 backdrop-blur md:p-14">
        {/* Atmosphere */}
        <div className="absolute -left-24 -top-24 size-72 rounded-full bg-nova/20 blur-[100px] transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
        <div className="absolute -bottom-32 -right-16 size-80 rounded-full bg-pulsar/20 blur-[100px]" aria-hidden="true" />
        <span
          className="text-outline-faint pointer-events-none absolute -bottom-8 right-4 hidden select-none font-display text-[11rem] font-extrabold leading-none lg:block"
          aria-hidden="true"
        >
          01
        </span>

        <div className="relative max-w-2xl">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-2 rounded-full bg-nova px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground">
              <Crown className="size-3.5" />
              {featuredProject.kicker}
            </span>
            <span className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Co-founder &amp; CTO
            </span>
          </div>

          <h3 className="mt-6 font-display text-4xl font-extrabold tracking-tight md:text-6xl">
            {featuredProject.name}
          </h3>
          <p className="mt-2 font-serif text-2xl italic text-flare md:text-3xl">
            {featuredProject.tagline}
          </p>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
            {featuredProject.description}
          </p>

          <div className="mt-7">
            <TagPills tags={featuredProject.tags} />
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export function Projects() {
  return (
    <section id="work" className="relative scroll-mt-28 py-28 md:py-36">
      <div className="container">
        <SectionHeading
          eyebrow="Selected work"
          title={
            <>
              Things she&apos;s{' '}
              <span className="font-serif font-normal italic text-nova">shipped.</span>
            </>
          }
          description="AI products, Web3 rails, and client platforms — all real, all in production or on testnet, all before turning twenty-one."
        />

        <div className="mt-14">
          <FeaturedCard />
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project, i) => (
            <Reveal key={project.name} delay={0.05 * (i % 3)}>
              <div className="group relative h-full overflow-hidden rounded-3xl border border-white/10 bg-card/60 p-7 backdrop-blur transition-all duration-300 hover:-translate-y-1.5 hover:border-white/25 hover:bg-card">
                <div
                  className={cn(
                    'pointer-events-none absolute -right-20 -top-20 size-52 rounded-full bg-gradient-to-br blur-2xl transition-opacity duration-500 group-hover:opacity-100',
                    project.gradient,
                  )}
                  aria-hidden="true"
                />

                <div className="relative flex h-full flex-col">
                  <div className="flex items-start justify-between">
                    <span className="font-display text-sm font-semibold text-muted-foreground/50">
                      {String(i + 2).padStart(2, '0')}
                    </span>
                    <span className="flex size-9 items-center justify-center rounded-full border border-white/10 text-muted-foreground transition-all duration-300 group-hover:border-white/40 group-hover:text-foreground">
                      <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </span>
                  </div>

                  <p className={cn('mt-5 text-xs font-semibold uppercase tracking-[0.25em]', project.accent)}>
                    {project.role}
                  </p>
                  <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">
                    {project.name}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {project.description}
                  </p>

                  <div className="mt-6">
                    <TagPills tags={project.tags} />
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Client work */}
        <Reveal className="mt-20">
          <div className="flex items-center gap-3">
            <Sparkles className="size-4 text-flare" />
            <h3 className="font-display text-xl font-semibold">Client work</h3>
            <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
            <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
              Production-grade
            </span>
          </div>
        </Reveal>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {clientWork.map((project, i) => (
            <Reveal key={project.name} delay={0.06 * i}>
              <div className="group relative h-full overflow-hidden rounded-3xl border border-dashed border-white/15 bg-card/40 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-white/35 hover:bg-card/70">
                <div
                  className={cn(
                    'pointer-events-none absolute -left-16 -top-16 size-44 rounded-full bg-gradient-to-br blur-2xl',
                    project.gradient,
                  )}
                  aria-hidden="true"
                />
                <div className="relative">
                  <p className={cn('text-xs font-semibold uppercase tracking-[0.25em]', project.accent)}>
                    {project.role}
                  </p>
                  <h4 className="mt-2 font-display text-xl font-bold tracking-tight">
                    {project.name}
                  </h4>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {project.description}
                  </p>
                  <div className="mt-5">
                    <TagPills tags={project.tags} />
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
